import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { useTeamsContext } from './ContextProviders/TeamsContextProvider';
import { useMyContext } from './ContextProviders/CreditsContextProvider';
import { createTeam, getUserTeams } from '../Requests/User/UserRequests';
import { clearTeamSpecificCaches } from '../utils/teamCacheManager';

export default function CreateTeamModal({ handleClose }) {
  const [open, setOpen] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { updateTeams } = useTeamsContext();
  const { setCredits } = useMyContext();

  const closeModal = () => {
    handleClose();
    setOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!teamName.trim()) {
      setError('Please enter a team name');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const response = await createTeam({
        name: teamName.trim()
      });
      
      if (response.data && response.data.team) {
        // Refresh teams list to include the new team
        const teamsResponse = await getUserTeams();
        
        if (teamsResponse.data && teamsResponse.data.teams) {
          updateTeams(teamsResponse.data.teams, response.data.team.uuid);
          setCredits(response.data.team.credits);
          
          // Clear team-specific cached data
          clearTeamSpecificCaches();
          
          // Close modal and refresh page
          closeModal();
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Failed to create team:', error);
      
      if (error.response?.status === 403) {
        setError('You do not have permission to create teams');
      } else if (error.response?.status === 400) {
        setError('Invalid team data. Please check the team name.');
      } else {
        setError('Failed to create team. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                    <UserGroupIcon
                      className="h-6 w-6 text-blue-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Create New Team
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Create a new team to organize your projects and collaborate with others.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-5 sm:mt-6">
                  <div>
                    <label htmlFor="team-name" className="block text-sm font-medium text-gray-700">
                      Team Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="team-name"
                        id="team-name"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        placeholder="Enter team name..."
                        required
                        autoFocus
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={isLoading || !teamName.trim()}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed sm:col-start-2 sm:text-sm"
                    >
                      {isLoading ? 'Creating...' : 'Create Team'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}